import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Label, ListBox, Select } from '@heroui/react';
import { DateRangeField } from '@/components/dateRangeField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { BranchFilterOption } from '../../shiftsMeta';
import {
    ALL_BRANCHES,
    type ShiftFilterState,
    type ShiftStatusFilter,
} from '../../shiftsMeta';

type ShiftFiltersProps = {
    filters: ShiftFilterState;
    branches: BranchFilterOption[];
    onRangeChange: (from: string, to: string) => void;
    onBranchChange: (branchId: string) => void;
    onStatusChange: (status: string) => void;
    /**
     * The register is still in flight. The controls keep their labels and shape
     * and are disabled — never replaced by a grey block, never removed.
     */
    isDisabled: boolean;
};

const STATUSES: ShiftStatusFilter[] = ['ALL', 'Closed', 'Open'];

const STATUS_LABEL_KEY: Record<ShiftStatusFilter, string> = {
    ALL: 'branches.shifts.filters.allStatuses',
    Closed: 'branches.shifts.status.closed',
    Open: 'branches.shifts.status.open',
};

/**
 * The register's filter row: a date range, a branch, a status.
 *
 * **The range bounds the shift's open day**, which is also what the register is
 * ordered by and what each row leads with — so a shift visible in the list can
 * never fall outside a window that appears to contain it.
 *
 * The branch select offers **every** branch, not only the ones the current page
 * happens to show: a filter built from the rows in front of you cannot narrow to
 * anything you cannot already see.
 */
export const ShiftFilters: React.FC<ShiftFiltersProps> = ({
    filters,
    branches,
    onRangeChange,
    onBranchChange,
    onStatusChange,
    isDisabled,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-wrap items-end gap-3"
            {...getNodeTestIdProp('branches-shifts-filters')}
        >
            <div className="w-full min-w-52 max-w-72">
                <DateRangeField
                    ariaLabel={t('branches.shifts.filters.rangeAria')}
                    fromDate={filters.from}
                    isDisabled={isDisabled}
                    onChange={onRangeChange}
                    slot="branches-shifts"
                    toDate={filters.to}
                />
            </div>

            <Select
                className="w-56"
                isDisabled={isDisabled}
                onChange={(next) => next && onBranchChange(String(next))}
                value={filters.branchId}
            >
                <Label className="text-xs text-muted">
                    {t('branches.shifts.filters.branch')}
                </Label>
                <Select.Trigger
                    {...getNodeTestIdProp('branches-shifts-filter-branch')}
                >
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        <ListBox.Item
                            id={ALL_BRANCHES}
                            textValue={t('branches.shifts.filters.allBranches')}
                        >
                            {t('branches.shifts.filters.allBranches')}
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                        {/* BOS-167: archived branches stay in a filter over
                            history — they are out of the pickers you *choose*
                            with, not out of the past. Chipped, the way Team
                            already marks an archived assignment. */}
                        {branches.map((branch) => (
                            <ListBox.Item
                                id={branch.id}
                                key={branch.id}
                                textValue={branch.name}
                            >
                                {branch.name}
                                {branch.archived ? (
                                    <Chip color="default" size="sm">
                                        {t('branches.shifts.filters.archived')}
                                    </Chip>
                                ) : null}
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Select.Popover>
            </Select>

            <Select
                className="w-44"
                isDisabled={isDisabled}
                onChange={(next) => next && onStatusChange(String(next))}
                value={filters.status}
            >
                <Label className="text-xs text-muted">
                    {t('branches.shifts.filters.status')}
                </Label>
                <Select.Trigger
                    {...getNodeTestIdProp('branches-shifts-filter-status')}
                >
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        {STATUSES.map((status) => (
                            <ListBox.Item
                                id={status}
                                key={status}
                                textValue={t(STATUS_LABEL_KEY[status])}
                            >
                                {t(STATUS_LABEL_KEY[status])}
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Select.Popover>
            </Select>
        </div>
    );
};
